import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/*
 * Full-screen photo lightbox: keyboard (Esc / arrows), touch swipe, counter.
 * images: [{ url, alt? }]
 */
export const Lightbox = ({ images, index, onClose, onIndex }) => {
  const { t } = useTranslation();
  const touchX = useRef(null);
  const [zoomed, setZoomed] = useState(false);

  const prev = useCallback(() => onIndex((index - 1 + images.length) % images.length), [index, images.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex]);

  useEffect(() => setZoomed(false), [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  if (index == null || !images[index]) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center eq-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={t("pub.gallery")}
      data-testid="lightbox"
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current == null || zoomed) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 48) (dx > 0 ? prev : next)();
        touchX.current = null;
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        data-testid="lightbox-close"
        className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors duration-200"
      >
        <X className="w-5 h-5" aria-hidden="true" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label={t("common.back")}
            data-testid="lightbox-prev"
            className="absolute left-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white hidden sm:flex items-center justify-center transition-colors duration-200"
          >
            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label={t("common.next")}
            data-testid="lightbox-next"
            className="absolute right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white hidden sm:flex items-center justify-center transition-colors duration-200"
          >
            <ChevronRight className="w-6 h-6" aria-hidden="true" />
          </button>
        </>
      )}

      <figure className={`max-w-[92vw] max-h-[88vh] ${zoomed ? "overflow-auto" : ""}`} onClick={(e) => e.stopPropagation()}>
        <img
          src={images[index].url}
          alt={images[index].alt || ""}
          data-testid="lightbox-image"
          onClick={() => setZoomed((z) => !z)}
          className={`max-w-[92vw] max-h-[82vh] object-contain rounded-lg select-none transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            zoomed ? "scale-[2] cursor-zoom-out" : "cursor-zoom-in"
          }`}
          draggable={false}
        />
        <figcaption className="mt-3 text-center text-xs text-white/60 tabular-nums" data-testid="lightbox-counter">
          {index + 1} / {images.length}
        </figcaption>
      </figure>
    </div>
  );
};
